/**
 * EditorAdapter — エディタ抽象（docs/03-editor.md §2）。
 *
 * この実装が守っている IME 規律:
 * - 上物（自動保存・ドラフトバッファ・アクセサリバー）は、どちらのエディタが載って
 *   いるかを知らない。IME の判定は `isComposing()` **ただ一つ**を通して行い、
 *   composition 判定のロジックを上物へ散らさない。
 * - `setValue` / `replaceRange` / `applyEdit` は composition 中に呼んではならない。
 *   各実装は呼ばれても無視する（安全側）ので、上物は「呼ばない」責務を持つ。
 */

import type { TextEdit } from '@shared/markdown-input';

export interface EditorAdapter {
  getValue(): string;
  /** 全置換。フォーカス外・非 composition 時のみ呼ぶ（docs/03 §4-6）。 */
  setValue(value: string): void;
  getSelection(): { start: number; end: number };
  replaceRange(
    from: number,
    to: number,
    text: string,
    newSelection?: { start: number; end: number },
  ): void;
  /** 選択行（複数可）のインデント / アウトデント。 */
  indentSelection(dir: 1 | -1): void;
  isComposing(): boolean;
  /** 値変更の購読。解除関数を返す。composition 中の入力でも発火する。 */
  onChange(cb: () => void): () => void;
  onCompositionEnd(cb: () => void): () => void;
  focus(): void;
  /** markdown-input.ts が返す TextEdit をそのまま適用する。 */
  applyEdit(edit: TextEdit): void;
}

/** どちらのエディタ実装が載っているか。localStorage に端末ごとに記憶する。 */
export type EditorKind = 'codemirror' | 'textarea';

/**
 * 個々のエディタ実装（TextareaEditor / CodeMirrorEditor）の共通 props。
 *
 * IME 規律: これらの実装は **props の変化で再描画しない**（`React.memo` の比較関数が
 * 常に true を返す）。値の差し替えやリセットは、必ず `key` を変えた再マウントで行う。
 * 変換中に React が再描画すると composition が壊れるため（docs/03 §2, §4-4）。
 */
export interface EditorViewProps {
  /** マウント時の初期値。マウント後の変更は無視される（上記の理由）。 */
  initialValue: string;
  /** マウント時に adapter を、アンマウント時に null を渡す。 */
  onReady: (adapter: EditorAdapter | null) => void;
  /** マウント時にフォーカスするか。 */
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * 実装間で共有する購読リスト。
 * 変換中でも発火するため、購読側は必要なら `isComposing()` を確認すること。
 */
export class Subscribers {
  private readonly set = new Set<() => void>();

  add(cb: () => void): () => void {
    this.set.add(cb);
    return () => {
      this.set.delete(cb);
    };
  }

  emit(): void {
    for (const cb of [...this.set]) cb();
  }

  clear(): void {
    this.set.clear();
  }
}

/**
 * composition 判定を保持する小さな状態。
 *
 * IME 規律（docs/03 §4-1、ADR-022）:
 * - **変換中かどうかの正は `compositionstart` 〜 `compositionend` だけ。**
 * - `keyCode === 229` を持続的な「変換中」として扱ってはいけない。
 *   Android Chrome + Gboard は**ほぼすべてのキー入力**（英数字も Enter も）で
 *   keyCode 229 を報告するため、229 で変換中フラグを立てると最初の1打鍵で
 *   立ちっぱなしになり、自動保存・入力補助・アクセサリバーがすべて沈黙する。
 *   2026-07-27 に実機で発生させた。229 は「直前の keydown が IME 由来か」という
 *   一時的なヒントとしてのみ保持し、`value` には寄与させない。
 * - `compositionend` 直後の短い猶予（`endedWithin`）は、確定 Enter が
 *   `beforeinput` の insertParagraph として遅れて届くブラウザの揺れに備えるためのもの。
 */
export class CompositionState {
  private composing = false;
  /** 直近の keydown が IME 由来（229）だったか。次の keydown まで有効な一時的ヒント。 */
  private lastKeydownWasIme = false;
  private endedAt = 0;

  /** 変換中か。判定の正は composition イベントのみ。 */
  get value(): boolean {
    return this.composing;
  }

  /** 直近の keydown が IME 由来だったか（keydown 時点の判断にのみ使う）。 */
  get imeKeydown(): boolean {
    return this.lastKeydownWasIme;
  }

  start(): void {
    this.composing = true;
  }

  end(): void {
    this.composing = false;
    this.endedAt = Date.now();
  }

  /**
   * 変換確定の直後か。
   * 確定 Enter が compositionend の後に insertParagraph として届くブラウザがあるため、
   * リスト継続などの「Enter に反応する処理」はこの窓の間は沈黙する。
   */
  endedWithin(ms: number): boolean {
    return this.endedAt !== 0 && Date.now() - this.endedAt < ms;
  }

  /** keydown を食わせる。戻り値は「この keydown 時点で変換中か」。 */
  observeKeydown(keyCode: number): boolean {
    this.lastKeydownWasIme = keyCode === 229;
    return this.composing;
  }

  /** フォーカスを失ったら状態を落とす。 */
  reset(): void {
    this.composing = false;
    this.lastKeydownWasIme = false;
  }
}

/** 確定 Enter がリスト継続へ漏れないようにする猶予（ミリ秒）。 */
export const COMPOSITION_END_GRACE_MS = 60;
